import React from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { displayedName } from '../../../data/publisherConfigs';
import { useStationHelpBranding } from './helpStationBranding';
import StationHelpModalHeader from './StationHelpModalHeader';
import { AdvertiserFaqList } from './advertiserFaq';
import { accentVars } from '../steps/_shared';
import * as analytics from '../../../lib/analytics';
import { EVENTS } from '../../../lib/analytics';

interface FaqModalProps {
  open: boolean;
  onClose: () => void;
  /** Prefer the Help menu's resolved station slug when params are unavailable. */
  stationSlug?: string;
}

const FaqModal: React.FC<FaqModalProps> = ({ open, onClose, stationSlug }) => {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const slug = stationSlug || paramSlug;
  const { config, accent, primary } = useStationHelpBranding(slug);

  if (!open) return null;

  const publisherName = config ? displayedName(config) : 'your station';
  const faqAccent = accent ?? '#64748b';
  const modalStyle = {
    fontFamily: config?.fontFamily,
    ...(accent ? accentVars(accent, primary) : {}),
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/45"
      role="dialog"
      aria-modal="true"
      aria-labelledby="faq-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col rounded-xl bg-white shadow-xl border border-slate-200"
        style={modalStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <StationHelpModalHeader config={config} onClose={onClose} />

        <div className="px-5 py-4 border-b border-slate-100 shrink-0">
          <h2 id="faq-modal-title" className="text-lg font-semibold text-slate-900">
            Frequently asked questions
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Quick answers about connected TV advertising on this platform.
          </p>
        </div>

        <div className="overflow-y-auto p-5">
          <h3
            className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500"
          >
            Your campaign
          </h3>
          <AdvertiserFaqList
            accentColor={faqAccent}
            publisherName={publisherName}
            devices={config?.devices}
            compact
          />
          {/* CTV-775: the AI commercial FAQ under its own subheading, below
              the general list. Not appended to the general list because that
              one also renders on the station landing page. */}
          <h3
            className="mt-6 mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500"
            data-testid="faq-ai-commercial-heading"
          >
            Your AI commercial
          </h3>
          <AdvertiserFaqList
            accentColor={faqAccent}
            publisherName={publisherName}
            compact
            faq="ai-commercial"
            onOpen={() => analytics.track(EVENTS.WAYMARK_FAQ_OPENED, { placement: 'help_modal' })}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FaqModal;
